// Voice adapter for the Companion's Talk button — GPT-Live-1 over WebRTC,
// native conversation mode with client delegation (product correction
// 2026-09-13, user 12959/12962).
//
// Live IS the conversation: its remote audio is audible and its output
// transcript is the visible spoken wording (greetings included). Collection
// lookups, prices, recommendations and record changes are delegated to the
// application backend (/ask), which owns the facts, the cards and the
// receipts shown on screen. The backend's compact verified facts/status go
// back to Live as `session.commentary.append` with the ORIGINAL opaque
// delegation id, at most ~500 tokens per append, and Live says them in its
// own voice. There is no separate TTS for Talk.
//
// Protocol per the official guides:
//   - `oai-events` channel created BEFORE the offer; ICE gathering waited
//     (rejects on timeout); POST offer SDP; apply answer; wait session.started
//     (deferred installed BEFORE the answer is applied) before "listening";
//   - never send session.start / response.create / conversation.item.create;
//   - transcript deltas are half-open intervals; duplicates and revisions are
//     handled by transcriptWindow.js; consumption is once, over
//     [previousOffset, W); offset 0 is valid; an EMPTY window is never
//     consumed and never moves the watermark;
//   - a delegation that arrives before its transcript waits boundedly for the
//     words; a delegation with no words is kept as "unanswered" and answered
//     exactly once by whichever comes first: the late transcript, the user's
//     explicit Send, or a Live retry;
//   - user speech during backend work never cancels that work (Live stops its
//     own audio); only cancelDelegation() cancels;
//   - end = session.close, then wait session.closed (listener registered
//     BEFORE sending), 15 s cap; every pending promise settles on teardown.

import { companionApi, newId } from './companionApi.js';
import { addSegment, consumeWindow, consumeAll, pendingText, recentText, hasPendingSpeech, classifyInput, utterances, retireAnswered } from './transcriptWindow.js';
import { toPlain } from './richText.js';
import { requestIdForDelegation } from './voiceIds.js';

const CHANNEL = 'oai-events';
const GATHER_TIMEOUT_MS = 10000;
const STARTED_TIMEOUT_MS = 15000;
const CLOSE_TIMEOUT_MS = 15000;
const COMMENTARY_TOKENS = 440;       // conservative budget under the 500-token cap per append
const SETTLE_QUIET_MS = 160;         // wait this long after the last transcript delta
const SETTLE_MAX_MS = 450;           // never wait longer than this for the boundary frame
const TRANSCRIPT_WAIT_MS = 1500;     // delegation before ASR: how long to wait for the words
const UNANSWERED_QUIET_MS = 3500;    // speech with neither delegation nor Live reply for this long → offer Send
const SPOKEN_GAP_MS = 2500;          // silence that separates two spoken utterances (a pause inside one reply is shorter)
const LIVE_ANSWER_GRACE_MS = 400;    // Live replied by voice: if no delegation follows within this, those words are answered
// Live docs (live-conversations): transcript fragments are not completed turns
// and a brief acknowledgement can sit inside an ongoing exchange; (live-
// delegation): the client keeps the task details itself. So a backchannel
// ("Okay", "Mhm", "let me check that") that Live utters while the user is
// still talking answers nothing: the words before it stay pending and the
// eventual delegation carries the whole request.
const BACKCHANNEL = /^(?:okay|ok|mhm|m+ ?hm+|uh ?huh|hm+|sure|right|alright|yes|yeah|yep|got it|i see|one (?:sec|second|moment)|just a (?:sec|second|moment)|hold on|on it|let me (?:check|see|look)(?: that| into that| on that)?(?: now)?|checking(?: that| on that)?(?: now)?)$/;
export function isBackchannel(text) {
  // Split on punctuation FIRST ("Okay, Mhm." → "okay" · "mhm"), then every
  // piece must be a complete provisional phrase; any other wording is a reply.
  const parts = toPlain(text || '').toLowerCase().split(/[.!?,;:…]+/).map(x => x.replace(/[^a-z' ]+/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean);
  return parts.length > 0 && parts.every(x => BACKCHANNEL.test(x));
}

function deferred() {
  let resolve, reject;
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
  return { promise, resolve, reject, settled: false };
}

// Conservative token estimate (no tokenizer in the bundle): every word piece,
// number group and punctuation mark counts, and long words count once per
// 4 characters. Over-estimates real BPE counts for prose, currencies and ids.
export function estimateTokens(text) {
  const s = String(text || '');
  let n = 0;
  for (const piece of s.split(/\s+/).filter(Boolean)) {
    const parts = piece.match(/[\p{L}]+|[\p{N}]+|[^\p{L}\p{N}\s]/gu) || [piece];
    for (const part of parts) n += Math.max(1, Math.ceil(part.length / 4));
  }
  return n;
}

// Cut at a word boundary so the estimate fits `maxTokens`.
export function compactCommentary(text, maxTokens = COMMENTARY_TOKENS) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (estimateTokens(s) <= maxTokens) return s;
  const words = s.split(' ');
  let out = '';
  for (const w of words) {
    const next = out ? `${out} ${w}` : w;
    if (estimateTokens(`${next} …`) > maxTokens) break;
    out = next;
  }
  return `${out} …`.trim();
}

// What the backend turn contributes to the conversation. The action status
// (receipt applied / edit only proposed) is the part Live must never guess at,
// so it is placed FIRST and always kept; the answer fills the rest of the budget.
export function commentaryForTurn(turn, maxTokens = COMMENTARY_TOKENS) {
  if (!turn) return '';
  let status = '';
  if (turn.receipt) status = `Verified change applied: ${compactCommentary(turn.receipt.summary || 'recorded with a receipt', 60)}.`;
  else if (turn.proposal) status = 'This edit is only proposed; nothing has changed until the user reviews the transcript and presses Send in the app.';
  const answer = String(turn.answer || turn.speech_text || '').trim();
  const room = Math.max(0, maxTokens - estimateTokens(status) - 1);
  const body = room > 0 ? compactCommentary(answer, room) : '';
  return [status, body].filter(Boolean).join(' ').trim();
}

export class VoiceAdapter extends EventTarget {
  constructor({ onDelegation } = {}) {
    super();
    this.state = 'idle'; this.reason = null;
    this.pc = null; this.stream = null; this.channel = null; this.audio = null;
    this.audioBlocked = false;
    this.sessionId = null; this.startedAt = null; this.usage = null;
    this.onDelegation = onDelegation;
    this.context = {};
    this.inputSegments = []; this.outputSegments = [];
    this.consumedUntil = 0; this.lastInputAt = 0; this.lastOutputAt = 0; this.lastNewSpeechAt = 0; this.commentarySpeechPending = false;
    this.currentRequestId = null;
    this.commentarySpeechPending = false; // an independent app announcement cannot answer overlapping user speech
    this.handledDelegations = new Map();  // delegation id → request id (answered or unanswered)
    this.answered = new Set();            // request ids that reached /ask (once-only)
    this.unanswered = null;               // { delegation_id, request_id, offset, context, at } waiting for words
    this.lastAnswered = null;             // { request_id, delegation_id, at } — a late re-delegation with no new words is a duplicate of this
    this.acks = new Map();
    this.delegationQueue = Promise.resolve();
    this.closing = false; this.stopped = false;
    this.timers = new Set(); this.generation = 0;
    this.pending = new Set(); // deferreds to settle on teardown
    this.started = null;      // deferred for session.started
    this.closed = null;       // deferred for session.closed
    this.spokenTimer = null; this.watchdog = null; this.liveAnswerTimer = null; this.inflight = 0;
    // Audio-clock origin on the wall clock, as an interval: audio cannot reach
    // Live before the answer SDP is applied (lower), and a transcript segment
    // cannot arrive before its audio ended (upper = min(arrival - end_ms)).
    this.clock = { lower: null, upper: null };
    // Local microphone speech onsets (wall clock, measured here, exact) and the
    // wall-clock arrival of each placed transcript segment. The scanner binds
    // a spoken correction to the scan active at the ONSET, not at ASR arrival.
    this.onsets = []; this.usedOnsets = new Set(); this.lastOffsetAt = 0; this.arrivals = new Map(); this.onsetDetector = null;
  }

  // Record a local speech onset (called by the analyser in the browser, or by
  // tests/harnesses directly).
  noteSpeechOnset(at = Date.now()) { this.onsets.push(at); this.emit('speech-onset', { at }); return at; }
  noteSpeechOffset(at = Date.now()) { this.lastOffsetAt = at; this.emit('speech-offset', { at }); }

  // Energy-based onset detector on the microphone stream: adaptive noise
  // floor, onset after 2 loud frames following >= 500 ms of quiet, offset
  // after 700 ms of quiet. Silent when the Web Audio API is unavailable.
  startOnsetDetector(stream) {
    const AC = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
    if (!AC || !stream) return null;
    try {
      const ac = new AC(); const src = ac.createMediaStreamSource(stream); const an = ac.createAnalyser(); an.fftSize = 1024; src.connect(an);
      // start() runs from the Talk/toggle gesture: resume now, and say so if it stays suspended.
      const report = () => this.emit('onset-detector', { state: ac.state, available: ac.state === 'running' });
      ac.resume().then(report, report); ac.onstatechange = report;
      const buf = new Float32Array(an.fftSize);
      let floor = 0.01, loud = 0, quietFrames = 20, speaking = false, quietSince = Date.now(), gateOpen = false;
      const tick = () => {
        an.getFloatTimeDomainData(buf);
        let sum = 0; for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length); const now = Date.now();
        if (!speaking) floor = Math.min(0.05, floor * 0.97 + rms * 0.03);
        const isLoud = rms > Math.max(0.02, floor * 3);
        if (isLoud) {
          loud++;
          // A new utterance begins after >= 500 ms of quiet, or after a short
          // pause (>= 150 ms) once the app has already consumed the earlier
          // speech: what follows a consumed request is a new request. The
          // quiet run is measured at the FIRST loud frame and held for the gate.
          if (loud === 1) { const consumedAll = this.onsets.length === 0 || this.onsets.every(at => this.usedOnsets.has(at)); gateOpen = quietFrames >= 10 || (consumedAll && quietFrames >= 3); }
          if (loud >= 2 && gateOpen && (!speaking || quietFrames === 0)) { speaking = true; gateOpen = false; this.noteSpeechOnset(now - 100); }
          quietFrames = 0; quietSince = now;
        } else {
          loud = 0; quietFrames++;
          if (speaking && now - quietSince >= 700) { speaking = false; this.noteSpeechOffset(now); }
        }
      };
      const id = setInterval(tick, 50);
      this.onsetDetector = { ac, stop: () => { clearInterval(id); try { src.disconnect(); ac.close(); } catch { /* ignore */ } } };
      return this.onsetDetector;
    } catch { return null; }
  }

  // Onsets that can belong to an utterance whose first transcript segment
  // arrived at `firstArrival`: after the previous utterance was consumed and
  // before that arrival (ASR always lags the microphone).
  onsetsFor(firstArrival, since) {
    const lo = Number.isFinite(since) ? since : 0, hi = Number.isFinite(firstArrival) ? firstArrival : Date.now();
    return this.onsets.filter(at => !this.usedOnsets.has(at) && at > lo && at <= hi);
  }

  // Factual context for Live (e.g. scanner mode on/off), never tied to a delegation.
  sendInstructions(content) {
    const event_id = newId();
    this.acks.set(event_id, false);
    const ok = this.send({ type: 'session.instructions.append', event_id, delegation_id: null, content: compactCommentary(content) });
    if (ok) this.timer(() => { if (this.acks.get(event_id) === false) this.emit('ack-missing', { event_id }); }, 8000);
    return ok ? event_id : null;
  }

  // The onset detector's audio context may be suspended by autoplay policy;
  // resume it from a user gesture and report the state visibly.
  async resumeOnsetDetector() {
    const d = this.onsetDetector; if (!d?.ac) return false;
    try { await d.ac.resume(); } catch { /* reported below */ }
    const ok = d.ac.state === 'running';
    this.emit('onset-detector', { state: d.ac.state, available: ok });
    return ok;
  }

  // Map an audio-timeline instant to a wall-clock interval. Null when unknown.
  audioToWall(ms) {
    if (!Number.isFinite(ms) || !Number.isFinite(this.clock.lower) || !Number.isFinite(this.clock.upper)) return null;
    return { lower: this.clock.lower + ms, upper: this.clock.upper + ms };
  }

  setState(state, reason = null) { this.state = state; this.reason = reason; this.dispatchEvent(new CustomEvent('state', { detail: { state, reason } })); }
  emit(type, detail) { this.dispatchEvent(new CustomEvent(type, { detail })); }
  timer(fn, ms) { const id = setTimeout(() => { this.timers.delete(id); fn(); }, ms); this.timers.add(id); return id; }
  cancelTimer(id) { if (id) { clearTimeout(id); this.timers.delete(id); } }
  clearTimers() { for (const id of this.timers) clearTimeout(id); this.timers.clear(); }
  track(d) { this.pending.add(d); d.promise.finally(() => this.pending.delete(d)).catch(() => {}); return d; }
  resolveD(d, v) { if (d && !d.settled) { d.settled = true; d.resolve(v); } }
  rejectD(d, e) { if (d && !d.settled) { d.settled = true; d.reject(e); } }

  static availabilityReason(capabilities) {
    if (!capabilities) return 'Checking what is available…';
    if (!capabilities.voice || capabilities.voice.available !== true) return capabilities?.voice?.reason || 'Talking to Bindarr is not available right now.';
    if (typeof window === 'undefined' || !window.RTCPeerConnection || !navigator.mediaDevices?.getUserMedia) return 'This browser cannot use the microphone here.';
    if (!window.isSecureContext) return 'Talking needs the https address.';
    return null;
  }

  inputText() { return recentText(this.inputSegments, 100000); }
  spokenText() { return recentText(this.outputSegments, 100000); }
  pendingSpeech() { return pendingText(this.inputSegments, this.consumedUntil); }
  hasUnansweredSpeech() { return hasPendingSpeech(this.inputSegments, this.consumedUntil); }

  // Concurrent callers (the Ask sheet and the scanner) share one startup:
  // while getUserMedia/SDP are in flight there is no pc yet, so the in-flight
  // promise is returned instead of starting a second microphone/session.
  start(opts = {}) {
    if (this.pc) return Promise.resolve(true);
    if (this.startPromise) return this.startPromise;
    this.startPromise = this.startSession(opts).finally(() => { this.startPromise = null; });
    return this.startPromise;
  }

  get starting() { return !!this.startPromise && !this.pc; }

  async startSession({ context = {}, capabilities } = {}) {
    const reason = VoiceAdapter.availabilityReason(capabilities);
    if (reason) { this.setState('unavailable', reason); return false; }
    if (this.pc) return true;
    const gen = ++this.generation;
    this.stopped = false; this.closing = false;
    this.context = context;
    this.inputSegments = []; this.outputSegments = []; this.consumedUntil = 0; this.usage = null;
    this.lastInputAt = 0; this.lastOutputAt = 0; this.lastNewSpeechAt = 0; this.commentarySpeechPending = false;
    this.handledDelegations = new Map(); this.answered = new Set(); this.unanswered = null; this.lastAnswered = null; this.acks = new Map();
    this.delegationQueue = Promise.resolve(); this.currentRequestId = null; this.audioBlocked = false; this.clock = { lower: null, upper: null };
    this.onsets = []; this.usedOnsets = new Set(); this.lastOffsetAt = 0; this.arrivals = new Map(); this.lastConsumedAt = 0;
    this.setState('connecting');
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (this.stopped || gen !== this.generation) { stream.getTracks().forEach(t => t.stop()); return false; }
      this.stream = stream;
      this.startOnsetDetector(stream);
      const pc = new RTCPeerConnection();
      this.pc = pc;
      // Native audio: Live's voice is the conversation. Audible, not muted.
      this.audio = document.createElement('audio');
      this.audio.autoplay = true; this.audio.muted = false; this.audio.volume = 1;
      this.audio.setAttribute('playsinline', ''); this.audio.style.display = 'none';
      document.body.appendChild(this.audio);
      pc.ontrack = (e) => {
        if (gen !== this.generation || !this.audio) return;
        this.audio.srcObject = e.streams?.[0] || new MediaStream([e.track]);
        this.playAudio();
      };
      for (const track of stream.getTracks()) pc.addTrack(track, stream);
      pc.onconnectionstatechange = () => {
        if (gen !== this.generation) return;
        if (['failed', 'disconnected', 'closed'].includes(pc.connectionState) && !this.closing && !this.stopped) this.fail('Connection lost.');
      };
      const channel = pc.createDataChannel(CHANNEL);
      this.channel = channel;
      channel.onmessage = (e) => { if (gen === this.generation) this.handleMessage(e.data); };
      channel.onerror = () => { if (gen === this.generation && !this.closing && !this.stopped) this.fail('Voice connection error.'); };
      channel.onclose = () => { if (gen === this.generation && !this.closing && !this.stopped && this.state !== 'closed') this.fail('Voice connection closed.'); };

      // Deferred for session.started exists BEFORE any SDP is applied, so an
      // event that arrives during setRemoteDescription resolves it.
      this.started = this.track(deferred());
      this.timer(() => this.rejectD(this.started, new Error('The voice session did not start in time.')), STARTED_TIMEOUT_MS);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await this.waitForIceGathering(pc);
      if (this.stopped || gen !== this.generation) return false;
      const res = await companionApi.voiceSession({ context: this.context, sdp: pc.localDescription.sdp });
      if (this.stopped || gen !== this.generation) return false;
      const answerSdp = res?.sdp || res?.transport?.sdp;
      if (!answerSdp) throw new Error('No voice session was returned.');
      this.sessionId = res.session_id || res?.session?.id || null;
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      if (this.stopped || gen !== this.generation) return false;
      this.clock.lower = Date.now();               // no audio reached Live before this
      await this.started.promise;
      if (this.stopped || gen !== this.generation) return false;
      this.startedAt = Date.now();
      if (!Number.isFinite(this.clock.upper)) this.clock.upper = this.startedAt;
      this.setState('listening');
      return true;
    } catch (err) {
      if (stream && !this.stream) stream.getTracks().forEach(t => t.stop());
      if (gen !== this.generation) return false; // torn down meanwhile; state already set
      const denied = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
      const msg = err?.status === 503 ? (err.message || 'Talking is not available right now.') : (err?.message || 'Could not start talking.');
      this.teardown();
      this.setState(denied ? 'unavailable' : 'error', denied ? 'Microphone permission was not granted.' : msg);
      return false;
    }
  }

  // Remote audio playback. If the browser blocks it (autoplay policy), the UI
  // offers a tap that calls playAudio() again; nothing is muted meanwhile.
  playAudio() {
    if (!this.audio) return Promise.resolve(false);
    this.audio.muted = false; this.audio.volume = 1;
    let p;
    try { p = this.audio.play?.(); } catch (err) { p = Promise.reject(err); }
    if (!p || typeof p.then !== 'function') { this.audioBlocked = false; return Promise.resolve(true); }
    return p.then(() => { if (this.audioBlocked) { this.audioBlocked = false; this.emit('audio', { blocked: false }); } return true; })
      .catch((err) => { this.audioBlocked = true; this.emit('audio', { blocked: true, message: err?.message || null }); return false; });
  }

  waitForIceGathering(pc) {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    const d = this.track(deferred());
    const check = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', check); this.resolveD(d); } };
    pc.addEventListener('icegatheringstatechange', check);
    this.timer(() => { pc.removeEventListener('icegatheringstatechange', check); this.rejectD(d, new Error('Network setup timed out.')); }, GATHER_TIMEOUT_MS);
    return d.promise;
  }

  // Bounded settle: resolve when transcript deltas have been quiet for
  // SETTLE_QUIET_MS, or after SETTLE_MAX_MS at most. Quiet is measured from
  // the later of the last delta and the moment the settle began, so a
  // delegation that lands before any transcript does not resolve at once.
  settleTranscript() {
    const startedAt = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        if (this.stopped || Date.now() - startedAt >= SETTLE_MAX_MS) return resolve();
        const quiet = Date.now() - Math.max(this.lastInputAt || 0, startedAt);
        if (quiet >= SETTLE_QUIET_MS) return resolve();
        this.timer(tick, Math.max(20, SETTLE_QUIET_MS - quiet));
      };
      tick();
    });
  }

  // Delegation before ASR: wait (bounded) until the transcript has words for
  // the window, then let them settle. Resolves true when words arrived.
  waitForTranscript(maxMs = TRANSCRIPT_WAIT_MS) {
    const startedAt = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        if (this.stopped) return resolve(false);
        if (this.hasUnansweredSpeech()) return resolve(true);
        if (Date.now() - startedAt >= maxMs) return resolve(false);
        this.timer(tick, 50);
      };
      tick();
    });
  }

  send(obj) {
    if (this.channel && this.channel.readyState === 'open') { try { this.channel.send(JSON.stringify(obj)); return true; } catch { return false; } }
    return false;
  }

  handleMessage(raw) {
    let ev = null;
    try { ev = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return; }
    if (!ev || typeof ev.type !== 'string') return;
    this.emit('event', ev);
    switch (ev.type) {
      case 'session.started':
        if (!Number.isFinite(this.clock.upper)) this.clock.upper = Date.now();
        if (!Number.isFinite(this.clock.lower)) this.clock.lower = this.clock.upper; // handler-driven tests: no SDP step
        this.resolveD(this.started);
        if (!this.closing && !this.stopped && this.state === 'connecting' && !this.started) this.setState('listening');
        break;
      case 'session.input_transcript.delta': {
        const seg = { delta: ev.delta ?? '', start_ms: ev.start_ms, end_ms: ev.end_ms };
        // Tighten the audio-clock origin: this text existed only after its audio ended.
        if (Number.isFinite(seg.end_ms)) { const bound = Date.now() - seg.end_ms; if (!Number.isFinite(this.clock.upper) || bound < this.clock.upper) this.clock.upper = bound; if (Number.isFinite(this.clock.lower) && this.clock.lower > this.clock.upper) this.clock.lower = this.clock.upper; }
        const kind = classifyInput(this.inputSegments, this.consumedUntil, seg);
        const before = this.inputSegments.length;
        addSegment(this.inputSegments, seg);
        if (Number.isFinite(seg.start_ms) && !this.arrivals.has(seg.start_ms)) this.arrivals.set(seg.start_ms, Date.now());
        this.lastInputAt = Date.now();
        const newSpeech = kind === 'new';
        if (newSpeech) this.lastNewSpeechAt = Date.now();
        // Caption is the utterance in progress (unconsumed text), not the session.
        // `newSpeech` is true only for genuinely new words: revisions, empty
        // frames and late fragments of an answered window are bookkeeping.
        if (this.inputSegments.length !== before || kind === 'revision') this.emit('user-speech', { text: this.pendingSpeech(), newSpeech, kind });
        this.armWatchdog();
        break;
      }
      case 'session.output_transcript.delta': {
        addSegment(this.outputSegments, { delta: ev.delta ?? '', start_ms: ev.start_ms, end_ms: ev.end_ms });
        this.lastOutputAt = Date.now();
        this.emitSpoken(false);
        this.armLiveAnswered();
        this.cancelTimer(this.spokenTimer);
        this.spokenTimer = this.timer(() => { this.spokenTimer = null; this.emitSpoken(true); }, SPOKEN_GAP_MS);
        break;
      }
      case 'session.thinking.appended':
      case 'session.commentary.appended':
      case 'session.instructions.appended':
        if (ev.client_event_id) { this.acks.set(ev.client_event_id, true); this.emit('ack', { event_id: ev.client_event_id }); }
        break;
      case 'session.delegation.created': {
        const id = ev.delegation?.id;
        if (id && this.handledDelegations.has(id)) {
          // A re-sent delegation: only useful if the earlier one is still unanswered.
          const rid = this.handledDelegations.get(id);
          if (!this.answered.has(rid) && this.unanswered && this.unanswered.request_id === rid) this.queueDelegation({ id, request_id: rid, offset: ev.offset_ms, retry: true });
          break;
        }
        const request_id = (id && requestIdForDelegation(id)) || newId();
        if (id) this.handledDelegations.set(id, request_id);
        this.queueDelegation({ id, request_id, offset: ev.offset_ms, retry: false });
        break;
      }
      case 'session.closed':
        this.usage = ev.usage ?? ev.session?.usage ?? null;
        this.emit('closed', { usage: this.usage });
        this.resolveD(this.closed, true);
        this.finishClose(true);
        break;
      case 'error':
      case 'session.error':
        if (!this.closing && !this.stopped) this.fail(ev.error?.message || ev.message || 'Voice session error.');
        break;
      default: break;
    }
  }

  // Native mode: when Live answers speech in its own voice (a greeting, a
  // clarification) and no delegation follows, those words are answered. They
  // are retired up to the start of Live's utterance so they never leak into
  // the next delegated question. Words spoken after Live started (barge-in)
  // stay pending. Never while a delegation is queued, in flight or unanswered.
  armLiveAnswered() {
    this.cancelTimer(this.liveAnswerTimer);
    this.liveAnswerTimer = this.timer(() => {
      this.liveAnswerTimer = null;
      if (this.stopped || this.closing || this.inflight > 0 || this.unanswered || this.state === 'delegating' || this.commentarySpeechPending) return;
      if (!this.hasUnansweredSpeech()) return;
      const list = utterances(this.outputSegments, SPOKEN_GAP_MS);
      const last = list[list.length - 1];
      if (!last) return;
      // A provisional acknowledgement ("Okay", "Mhm", "let me check that") is a
      // backchannel inside one exchange (exact native trace: "Rosa's encouragement,
      // from the Pokémon set Perfect Order" · Live "Okay, Mhm." · "I have four normal
      // copies" → one delegation): nothing is answered, nothing is retired.
      // A full reply (a greeting sentence) keeps the ordinary retirement.
      // No arrival-order condition: the next clause's ASR may land after this
      // grace timer, which is exactly the loss race. An acknowledgement alone is
      // never proof the user's turn finished; the words wait for a substantive
      // reply or the delegation.
      if (isBackchannel(last.text)) return;
      // Words before Live's reply, plus the late ASR tail of that same
      // utterance (contiguous on the timeline); a barge-in after a pause stays.
      const first = this.inputSegments.filter(s => !s.consumed && Number.isFinite(s.start_ms) && s.start_ms >= this.consumedUntil).sort((a, b) => a.start_ms - b.start_ms)[0];
      const win = retireAnswered(this.inputSegments, this.consumedUntil, last.start_ms);
      if (win.empty) return;
      // A native greeting consumes microphone evidence just like a delegated
      // turn. Otherwise its stale onset precedes the next correction's onset
      // and binds that correction to the greeting's (often absent) scan.
      // Bound by the FIRST transcript arrival, not retirement time: a later
      // barge-in onset must remain available even when this timer runs late.
      if (first) this.speechTiming({ start_ms: first.start_ms, end_ms: win.consumedUntil });
      this.consumedUntil = win.consumedUntil;
      this.commentarySpeechPending = false;
      this.emit('user-speech', { text: this.pendingSpeech(), newSpeech: false, kind: 'live-answered', answered: win.text });
    }, LIVE_ANSWER_GRACE_MS);
  }

  // Live's spoken wording, grouped into utterances; the last one is the
  // caption. `final` when the gap timer elapsed.
  emitSpoken(final) {
    const list = utterances(this.outputSegments, SPOKEN_GAP_MS);
    const last = list[list.length - 1];
    if (!last) return;
    // Spoken wording is shown as plain speech: stage directions such as
    // "[sigh]" and any markup are stripped (same rule as answer rendering).
    this.emit('assistant-speech', { utterance_id: `${this.sessionId || 'live'}-${last.id}`, text: toPlain(last.text), final, start_ms: last.start_ms, end_ms: last.end_ms });
  }

  // Context is frozen at the delegation. The transcript window is resolved
  // after a bounded settle (and a bounded wait when the words have not
  // arrived yet), then consumed exactly once.
  queueDelegation({ id, request_id, offset, retry }) {
    const frozenContext = { ...this.context };
    this.inflight++;
    this.delegationQueue = this.delegationQueue.then(async () => {
      if (this.stopped || this.closing) return;
      if (this.answered.has(request_id)) return; // already asked (late duplicate)
      await this.settleTranscript();
      // An offset at or before the answered watermark points at speech that
      // was already answered (a re-delegation after an explicit Send or a
      // retry). It must never wait for, or take, the NEXT question's words.
      const offsetAnswered = Number.isFinite(offset) && offset <= this.consumedUntil && this.consumedUntil > 0;
      if (offsetAnswered && !retry && !this.hasUnansweredSpeech()) {
        if (id && this.lastAnswered) this.handledDelegations.set(id, this.lastAnswered.request_id);
        this.emit('delegation-duplicate', { request_id: this.lastAnswered?.request_id || null, delegation_id: id });
        this.sendCommentary(id, 'That question was already answered; the facts already provided stand.');
        return;
      }
      let win = consumeWindow(this.inputSegments, this.consumedUntil, offset);
      if (win.empty && !win.invalidOffset && !this.hasUnansweredSpeech()) {
        // Delegation before ASR: wait for the words, then settle again.
        if (await this.waitForTranscript()) { await this.settleTranscript(); win = consumeWindow(this.inputSegments, this.consumedUntil, offset); }
      }
      if (win.empty && this.hasUnansweredSpeech()) {
        // Words exist but sit past the offset (offset pointed at silence, or
        // the delegation is a retry): take everything unanswered, once.
        win = consumeAll(this.inputSegments, this.consumedUntil);
      }
      if (win.empty) {
        // Nothing to answer yet. Keep this delegation as "unanswered": the late
        // transcript, an explicit Send or a Live retry answers it exactly once.
        this.unanswered = { delegation_id: id, request_id, offset, context: frozenContext, at: Date.now(), retry: !!retry };
        this.emit('delegation-empty', { request_id, delegation_id: id, invalidOffset: win.invalidOffset, pending: this.pendingSpeech() });
        this.sendCommentary(id, 'No new question was heard yet; if the user asked something, ask them to repeat it briefly.');
        this.armWatchdog();
        return;
      }
      this.consumedUntil = win.consumedUntil;
      this.commentarySpeechPending = false;
      this.emit('user-speech', { text: this.pendingSpeech(), newSpeech: false, kind: 'consumed' });
      if (this.unanswered && this.unanswered.request_id !== request_id && !this.answered.has(this.unanswered.request_id)) {
        // The words that arrived belonged to the earlier, unanswered delegation: answer under ITS id once.
        const u = this.unanswered; this.unanswered = null;
        return this.handleDelegation({ request_id: u.request_id, delegation_id: u.delegation_id, message: win.text, context: u.context, history: { input: recentText(this.inputSegments), output: this.spokenText().slice(-1200) }, speech: this.speechTiming(win) });
      }
      this.unanswered = null;
      return this.handleDelegation({ request_id, delegation_id: id, message: win.text, context: frozenContext, history: { input: recentText(this.inputSegments), output: this.spokenText().slice(-1200) }, speech: this.speechTiming(win) });
    }).catch(() => {}).finally(() => { this.inflight = Math.max(0, this.inflight - 1); });
  }

  // Explicit Send from the UI: submit the unanswered speech now. Uses the
  // unanswered delegation's ids when there is one, otherwise a fresh request
  // with no delegation id (Live then hears the facts as plain commentary).
  // Once-only with any late Live delegation for the same words.
  sendPending() {
    const frozenContext = { ...this.context };
    this.delegationQueue = this.delegationQueue.then(async () => {
      if (this.stopped || this.closing) return null;
      const win = consumeAll(this.inputSegments, this.consumedUntil);
      if (win.empty) { this.emit('delegation-empty', { request_id: null, delegation_id: null, invalidOffset: false, pending: '' }); return null; }
      this.consumedUntil = win.consumedUntil;
      this.commentarySpeechPending = false;
      this.emit('user-speech', { text: this.pendingSpeech(), newSpeech: false, kind: 'consumed' });
      const u = this.unanswered && !this.answered.has(this.unanswered.request_id) ? this.unanswered : null;
      this.unanswered = null;
      const request_id = u ? u.request_id : newId();
      return this.handleDelegation({ request_id, delegation_id: u ? u.delegation_id : null, message: win.text, context: u ? u.context : frozenContext, history: { input: recentText(this.inputSegments), output: this.spokenText().slice(-1200) }, explicit: true, speech: this.speechTiming(win) });
    }).catch(() => null);
    return this.delegationQueue;
  }

  // Speech that got neither a delegation nor a spoken reply for a while is
  // surfaced so the user can Send it; nothing is submitted automatically.
  armWatchdog() {
    this.cancelTimer(this.watchdog);
    this.watchdog = this.timer(() => {
      this.watchdog = null;
      if (this.stopped || this.closing || this.state === 'delegating') return;
      if (!this.hasUnansweredSpeech()) return;
      const since = Math.max(this.lastInputAt, this.lastNewSpeechAt);
      if (this.lastOutputAt > since && !this.commentarySpeechPending) return; // an independent scan announcement is not an answer
      if (Date.now() - since < UNANSWERED_QUIET_MS) { this.armWatchdog(); return; }
      if (this.commentarySpeechPending) { this.sendPending(); return; } // existing once-only client delegation, not a keyword router
      this.emit('unanswered-speech', { text: this.pendingSpeech(), had_delegation: !!this.unanswered });
    }, UNANSWERED_QUIET_MS + 50);
  }

  // When the words were spoken: audio-timeline start/end and the wall-clock
  // interval they map to (bound NOW, at consumption, never later).
  speechTiming(win) {
    const firstArrival = Number.isFinite(win.start_ms) ? (this.arrivals.get(win.start_ms) ?? null) : null;
    const onsets = this.onsetsFor(firstArrival ?? Date.now(), this.lastConsumedAt);
    for (const at of onsets) this.usedOnsets.add(at);
    // Later onsets (spoken after this utterance arrived) belong to the next one.
    this.lastConsumedAt = Math.max(this.lastConsumedAt, ...onsets, 0);
    return { start_ms: win.start_ms ?? null, end_ms: win.end_ms ?? null, wall: this.audioToWall(win.start_ms), first_arrival: firstArrival, onsets };
  }

  async handleDelegation(frozen) {
    if (this.stopped || this.closing) return null;
    const gen = this.generation;
    const { request_id, delegation_id, message, context, history, speech } = frozen;
    if (this.answered.has(request_id)) return null;
    this.answered.add(request_id);
    this.lastAnswered = { request_id, delegation_id, at: Date.now() };
    this.currentRequestId = request_id;
    this.setState('delegating');
    this.emit('delegation', { request_id, delegation_id, message, explicit: !!frozen.explicit, speech });
    try {
      const turn = await this.onDelegation?.({ request_id, delegation_id, message, context, input_mode: 'voice', history, speech });
      if (gen !== this.generation) return turn ?? null;
      if (turn && this.currentRequestId === request_id) {
        this.sendCommentary(delegation_id, commentaryForTurn(turn));
        this.emit('delegation-result', { request_id, delegation_id, turn });
      } else if (this.currentRequestId !== request_id) {
        this.sendCommentary(delegation_id, 'That request was cancelled by the user.');
      } else {
        this.sendCommentary(delegation_id, 'Bindarr could not answer that just now; say so and offer to try again.');
        this.emit('delegation-error', { request_id, delegation_id, message: null });
      }
      return turn ?? null;
    } catch (err) {
      if (gen !== this.generation) return null;
      this.sendCommentary(delegation_id, 'Bindarr could not answer that just now; say so and offer to try again.');
      this.emit('delegation-error', { request_id, delegation_id, message: err?.message || null });
      return null;
    } finally {
      if (gen === this.generation) {
        if (this.currentRequestId === request_id) this.currentRequestId = null;
        if (this.state === 'delegating') this.setState('listening');
      }
    }
  }

  // Compact verified facts/status for Live, tied to the original delegation.
  sendCommentary(delegation_id, content) {
    const event_id = newId();
    this.acks.set(event_id, false);
    const overlapping = delegation_id == null && (this.hasUnansweredSpeech() || this.onsets.some(at => !this.usedOnsets.has(at)));
    const deliver = () => {
      const ok = this.send({ type: 'session.commentary.append', event_id, delegation_id: delegation_id ?? null, content: compactCommentary(content) });
      if (ok) this.timer(() => { if (this.acks.get(event_id) === false) this.emit('ack-missing', { event_id }); }, 8000);
    };
    if (overlapping) {
      // Barge-in: an independent save stays committed/on-screen, but its audio
      // cannot interrupt or contextually rewrite the user's current correction.
      this.commentarySpeechPending = true;
      this.armWatchdog();
      const flush = () => {
        if (this.stopped || this.closing) return;
        if (this.commentarySpeechPending || this.inflight > 0 || this.state === 'delegating') { this.timer(flush, 100); return; }
        deliver();
      };
      this.timer(flush, 100);
    } else deliver();
    return event_id;
  }

  async cancelDelegation() {
    const id = this.currentRequestId;
    if (!id) return null;
    this.currentRequestId = null;
    try { return await companionApi.cancel(id); } catch (err) { return { request_id: id, status: 'error', error: err?.message }; }
  }

  elapsedSeconds() { return this.startedAt ? Math.round((Date.now() - this.startedAt) / 1000) : 0; }

  async end() {
    // A startup still in flight (no pc yet) is cancelled: the start loop sees
    // `stopped` after each await and releases the microphone.
    if (!this.pc && this.startPromise) { this.stop(null); return { usage: null, confirmed: false, cancelled: true }; }
    if (!this.pc || this.closing) { if (!this.pc) this.setState('closed'); return { usage: this.usage, confirmed: false }; }
    this.closing = true;
    this.setState('closing');
    // Listener/deferred first, then the close command: a fast session.closed
    // cannot be missed.
    this.closed = this.track(deferred());
    this.timer(() => this.resolveD(this.closed, false), CLOSE_TIMEOUT_MS);
    const sent = this.send({ type: 'session.close', event_id: newId() });
    if (!sent) { this.resolveD(this.closed, false); this.finishClose(false); return { usage: null, confirmed: false }; }
    const confirmed = await this.closed.promise.catch(() => false);
    if (!confirmed) this.finishClose(false);
    return { usage: this.usage, confirmed };
  }

  finishClose(confirmed) {
    if (!this.pc && this.state === 'closed') return;
    this.emitSpoken(true);
    this.teardown();
    this.setState('closed', confirmed ? null : 'Call ended; talk time not confirmed.');
  }

  fail(reason) { this.teardown(); this.setState('error', reason); }

  teardown() {
    this.generation++;
    this.clearTimers(); this.spokenTimer = null; this.watchdog = null; this.liveAnswerTimer = null; this.inflight = 0;
    // Settle everything anyone might be awaiting.
    for (const d of [...this.pending]) this.rejectD(d, new Error('Voice session ended.'));
    this.pending.clear(); this.started = null; this.closed = null;
    if (this.channel) { try { this.channel.onmessage = null; this.channel.onerror = null; this.channel.onclose = null; this.channel.close(); } catch { /* ignore */ } }
    if (this.pc) { try { this.pc.ontrack = null; this.pc.onconnectionstatechange = null; this.pc.close(); } catch { /* ignore */ } }
    if (this.onsetDetector) { try { this.onsetDetector.stop(); } catch { /* ignore */ } this.onsetDetector = null; }
    if (this.stream) { try { this.stream.getTracks().forEach(t => t.stop()); } catch { /* ignore */ } }
    if (this.audio) { try { this.audio.pause?.(); this.audio.srcObject = null; this.audio.remove(); } catch { /* ignore */ } }
    this.channel = null; this.pc = null; this.stream = null; this.audio = null;
    this.startedAt = null; this.closing = false; this.currentRequestId = null;
  }

  stop(reason = null) { this.stopped = true; this.teardown(); this.setState('closed', reason); }
}
